import React, { useState, useEffect } from "react";
import "../style/AddressModal.css";

import { postUserLocation } from "../../../../api/post/postUserLocation";
import { getCountries } from "../../../../api/location/getCountries";
import { getRegions } from "../../../../api/location/getRegions";
import { getDistricts } from "../../../../api/location/getDistricts";
import { getLocalizedName } from "../../../../api/location/locationUtils";

import closeIcon from '../../../../assets/icons/close.svg';

import { t } from "i18next";

function AddressModal({ onClose, onSubmitted, mode = "add" }) {
    const [countries, setCountries] = useState([]);
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    
    const [street, setStreet] = useState("");
    const [house, setHouse] = useState("");
    
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // Load countries on mount
    useEffect(() => {
        const loadCountries = async () => {
            try {
                setIsLoading(true);
                const countriesData = await getCountries();
                setCountries(countriesData);
            } catch (error) {
                console.error("Failed to load countries:", error);
                setErrorMessage(t('failed_to_load_countries') || "Не удалось загрузить страны");
            } finally {
                setIsLoading(false);
            }
        };
        
        loadCountries();
    }, []);

    // Load regions when country is selected
    useEffect(() => {
        const loadRegions = async () => {
            if (!selectedCountry) {
                setRegions([]);
                setDistricts([]);
                setSelectedRegion(null);
                setSelectedDistrict(null);
                return;
            }

            try {
                setIsLoading(true);
                const regionsData = await getRegions(selectedCountry.id);
                setRegions(regionsData);
                setDistricts([]);
                setSelectedRegion(null);
                setSelectedDistrict(null);
            } catch (error) {
                console.error("Failed to load regions:", error);
                setErrorMessage(t('failed_to_load_regions') || "Не удалось загрузить регионы");
            } finally {
                setIsLoading(false);
            }
        };

        loadRegions();
    }, [selectedCountry]);

    // Load districts when region is selected
    useEffect(() => {
        const loadDistricts = async () => {
            if (!selectedRegion) {
                setDistricts([]);
                setSelectedDistrict(null);
                return;
            }

            try {
                setIsLoading(true);
                const districtsData = await getDistricts(selectedRegion.id);
                setDistricts(districtsData);
                setSelectedDistrict(null);
            } catch (error) {
                console.error("Failed to load districts:", error);
                setErrorMessage(t('failed_to_load_districts') || "Не удалось загрузить районы");
            } finally {
                setIsLoading(false);
            }
        };

        loadDistricts();
    }, [selectedRegion]);

    const handleCountryChange = (e) => {
        const countryId = parseInt(e.target.value);
        const country = countries.find(c => c.id === countryId);
        setSelectedCountry(country || null);
    };

    const handleRegionChange = (e) => {
        const regionId = parseInt(e.target.value);
        const region = regions.find(r => r.id === regionId);
        setSelectedRegion(region || null);
    };

    const handleDistrictChange = (e) => {
        const districtId = parseInt(e.target.value);
        const district = districts.find(d => d.id === districtId);
        setSelectedDistrict(district || null);
    };

    const handleApply = async () => {
        // Validate required fields
        if (!selectedCountry) {
            setErrorMessage(t('select_country') || "Выберите страну");
            return;
        }
        if (!selectedRegion) {
            setErrorMessage(t('select_region') || "Выберите регион");
            return;
        }
        if (!selectedDistrict) {
            setErrorMessage(t('select_district') || "Выберите район");
            return;
        }

        const addressData = {
            country_id: selectedCountry.id,
            region_id: selectedRegion.id,
            district_id: selectedDistrict.id,
            country: getLocalizedName(selectedCountry),
            province: getLocalizedName(selectedRegion),
            locality: getLocalizedName(selectedDistrict),
            street: street || "",
            house: house || "",
        };

        try {
            setIsLoading(true);
            setErrorMessage("");
            
            if (mode === "add") {
                await postUserLocation(addressData);
                window.location.reload();
            } else if (mode === "select") {
                onSubmitted(addressData);
            }
            onClose();
        } catch (error) {
            console.error("Failed to handle address data:", error);
            setErrorMessage(t('failed_to_save_location') || "Не удалось сохранить адрес");
        } finally {
            setIsLoading(false);
        }
    };

    const currentLang = localStorage.getItem('language') || 'ru';

    return (
        <div className="location_modal">
            <div className="modal_header">
                <h2>{t('location')}</h2>
                <button className="click mobile_hide navigation_close" onClick={onClose}>
                    <img src={closeIcon} alt="Close" />
                </button>
            </div>

            <div className="location_form_content">
                <div className="location_field">
                    <label className="location_label">{t('country') || "Страна"}</label>
                    <select
                        className="location_select"
                        value={selectedCountry?.id || ""}
                        onChange={handleCountryChange}
                        disabled={isLoading}
                    >
                        <option value="">{t('select_country') || "Выберите страну"}</option>
                        {countries.map(country => (
                            <option key={country.id} value={country.id}>
                                {getLocalizedName(country, currentLang)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="location_field">
                    <label className="location_label">{t('region') || "Регион"}</label>
                    <select
                        className="location_select"
                        value={selectedRegion?.id || ""}
                        onChange={handleRegionChange}
                        disabled={!selectedCountry || isLoading}
                    >
                        <option value="">{t('select_region') || "Выберите регион"}</option>
                        {regions.map(region => (
                            <option key={region.id} value={region.id}>
                                {getLocalizedName(region, currentLang)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="location_field">
                    <label className="location_label">{t('district') || "Район"}</label>
                    <select
                        className="location_select"
                        value={selectedDistrict?.id || ""}
                        onChange={handleDistrictChange}
                        disabled={!selectedRegion || isLoading}
                    >
                        <option value="">{t('select_district') || "Выберите район"}</option>
                        {districts.map(district => (
                            <option key={district.id} value={district.id}>
                                {getLocalizedName(district, currentLang)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="location_field">
                    <label className="location_label">{t('street') || "Улица"}</label>
                    <input
                        type="text"
                        className="location_input"
                        placeholder={t('enter_street') || "Введите улицу"}
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                    />
                </div>

                <div className="location_field">
                    <label className="location_label">{t('house') || "Дом"}</label>
                    <input
                        type="text"
                        className="location_input"
                        placeholder={t('enter_house') || "Введите номер дома"}
                        value={house}
                        onChange={(e) => setHouse(e.target.value)}
                    />
                </div>

                {errorMessage && (
                    <div className="location_error">
                        <p>{errorMessage}</p>
                    </div>
                )}
            </div>

            <button 
                className="submit_button" 
                onClick={handleApply}
                disabled={isLoading || !selectedCountry || !selectedRegion || !selectedDistrict}
            >
                {isLoading ? (t('loading') || "Загрузка...") : (mode === "add" ? t('add') : t('apply'))}
            </button>
        </div>
    );
}

export default AddressModal;